import { GameV0 } from '@/types';
import { Game } from '../types/games_api_types';
import React, { useState, useEffect, useCallback } from 'react';
import classes from './BaseballSoftballGameControl.module.css';
import GameScorecard from '../game/GameScorecard';

type InningPart = 'Top' | 'Mid' | 'Bot' | 'End';

export default function BaseballSoftballGameControl({
  uid,
  sport,
}: {
  uid: string;
  sport: 'baseball' | 'softball';
}) {
  const [game, setGame] = useState<Game | null>(null);
  const [inningPart, setInningPart] = useState<InningPart>('Top');
  const [inningNumber, setInningNumber] = useState<number>(1);
  const [awayScores, setAwayScores] = useState<(number | null)[]>([]);
  const [homeScores, setHomeScores] = useState<(number | null)[]>([]);
  const [maxInnings, setMaxInnings] = useState<number>(9);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [currentRunners, setCurrentRunners] = useState<
    [boolean, boolean, boolean]
  >([false, false, false]);
  const [currentOuts, setCurrentOuts] = useState<number>(0);

  useEffect(() => {
    const loadGame = async () => {
      const response = await fetch(`/api/v1/games/${sport}/${uid}.json`);
      const gameData = await response.json();
      setGame(gameData);

      // Initialize inning scores
      const maxLen = Math.max(
        gameData.away_score_array?.length || 0,
        gameData.home_score_array?.length || 0,
      );
      setAwayScores(gameData.away_score_array || []);
      setHomeScores(gameData.home_score_array || []);
      setMaxInnings(Math.max(maxLen, sport === 'baseball' ? 9 : 7));

      // Parse current inning from status_text if in progress
      if (
        gameData?.state === 'in_progress' &&
        gameData.status_text &&
        gameData.status_text !== 'In Progress'
      ) {
        const parts = gameData.status_text.split(' ');
        if (parts.length === 2) {
          const part = parts[0] as InningPart;
          const inning = parseInt(parts[1], 10);
          if (
            !isNaN(inning) &&
            ['Top', 'Mid', 'Bot', 'End'].indexOf(part as string) >= 0
          ) {
            setInningPart(part);
            setInningNumber(inning);
          } else {
            setInningPart('Top');
            setInningNumber(1);
          }
        }
      }

      // Load runners and outs if available (now on top-level game object)
      if ((gameData as any)?.current_runners) {
        setCurrentRunners((gameData as any).current_runners);
      }
      if (
        (gameData as any)?.current_outs !== undefined &&
        (gameData as any)?.current_outs !== null
      ) {
        setCurrentOuts((gameData as any).current_outs);
      }
    };

    loadGame();
  }, [sport, uid]);

  const startGame = useCallback(async () => {
    const response = await fetch(`/admin/${sport}/games/${uid}/start.json`, {
      method: 'POST',
    });
    setGame(await response.json());
    setInningPart('Top');
    setInningNumber(1);
  }, [sport, uid]);

  const finishGame = useCallback(async () => {
    const response = await fetch(`/admin/${sport}/games/${uid}/finish.json`, {
      method: 'POST',
    });
    if (response.ok) {
      setGame(await response.json());
    }
  }, [sport, uid]);

  if (game === null) {
    return null;
  }

  const handleInningScoreChange = (
    inningIndex: number,
    team: 'away' | 'home',
    value: string,
  ) => {
    const scores = team === 'away' ? [...awayScores] : [...homeScores];
    scores[inningIndex] = value === '' ? null : parseInt(value, 10);
    if (team === 'away') {
      setAwayScores(scores);
    } else {
      setHomeScores(scores);
    }
  };

  const addExtraInning = () => {
    setMaxInnings(maxInnings + 1);
  };

  const handleSubmitScores = async () => {
    setIsSubmitting(true);
    try {
      // Pad arrays to maxInnings length with nulls
      const paddedAwayScores = [...awayScores];
      const paddedHomeScores = [...homeScores];

      while (paddedAwayScores.length < maxInnings) {
        paddedAwayScores.push(null);
      }
      while (paddedHomeScores.length < maxInnings) {
        paddedHomeScores.push(null);
      }

      const response = await fetch(
        `/admin/${sport}/games/${uid}/update_score_and_inning.json`,
        {
          method: 'POST',
          body: JSON.stringify({
            away_scores: paddedAwayScores,
            home_scores: paddedHomeScores,
            inning_part: inningPart,
            inning_number: inningNumber,
            current_runners: currentRunners,
            current_outs: currentOuts,
          }),
        },
      );

      if (response.ok) {
        setGame(await response.json());
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
        }, 2000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalAwayScore = awayScores.reduce(
    (sum: number, score) => sum + (score || 0),
    0,
  );
  const totalHomeScore = homeScores.reduce(
    (sum: number, score) => sum + (score || 0),
    0,
  );

  return (
    <>
      <div className="tray" style={{ marginBottom: 25 }}>
        <div className="inner-tray">
          <GameScorecard game={game} />
        </div>
      </div>
      <div className={classes.controlCenter}>
        {game.last_tracked_at !== null && (
          <div className={classes.lastTracked}>
            Last Tracked:{' '}
            {new Intl.DateTimeFormat('en-US', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }).format(new Date(game.last_tracked_at * 1000))}
          </div>
        )}

        <div className={classes.scoreKeeper}>
          {game.state == 'upcoming' && (
            <button
              className="btn btn-success btn-lg"
              onClick={() => {
                startGame();
              }}
            >
              Start
            </button>
          )}

          {game.state === 'in_progress' && (
            <>
              <div className={classes.inningScoresTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Team</th>
                      {Array.from({ length: maxInnings }, (_, i) => (
                        <th key={i}>{i + 1}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={classes.teamLabel}>{game.away_team}</td>
                      {Array.from({ length: maxInnings }, (_, i) => (
                        <td key={i}>
                          <input
                            type="number"
                            min="0"
                            value={awayScores[i] ?? ''}
                            onChange={(e) =>
                              handleInningScoreChange(i, 'away', e.target.value)
                            }
                            placeholder="-"
                          />
                        </td>
                      ))}
                      <td className={classes.totalScore}>{totalAwayScore}</td>
                    </tr>
                    <tr>
                      <td className={classes.teamLabel}>{game.home_team}</td>
                      {Array.from({ length: maxInnings }, (_, i) => (
                        <td key={i}>
                          <input
                            type="number"
                            min="0"
                            value={homeScores[i] ?? ''}
                            onChange={(e) =>
                              handleInningScoreChange(i, 'home', e.target.value)
                            }
                            placeholder="-"
                          />
                        </td>
                      ))}
                      <td className={classes.totalScore}>{totalHomeScore}</td>
                    </tr>
                  </tbody>
                </table>
                <div className={classes.tableButtons}>
                  <button
                    className={classes.addInningButton}
                    onClick={addExtraInning}
                  >
                    + Extra Inning
                  </button>
                  <div className={classes.inningControlsInline}>
                    <div className={classes.controlGroup}>
                      <label>Inning Part:</label>
                      <select
                        value={inningPart}
                        onChange={(e) => {
                          const newPart = e.target.value as InningPart;
                          setInningPart(newPart);
                        }}
                      >
                        <option value="Top">Top</option>
                        <option value="Mid">Mid</option>
                        <option value="Bot">Bot</option>
                        <option value="End">End</option>
                      </select>
                    </div>
                    <div className={classes.controlGroup}>
                      <label>Inning:</label>
                      <input
                        type="number"
                        value={inningNumber}
                        onChange={(e) => {
                          const newInning = parseInt(e.target.value, 10);
                          if (newInning >= 1) {
                            setInningNumber(newInning);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className={classes.baserunnerControls}>
                  <div className={classes.controlGroup}>
                    <label>Baserunners:</label>
                    <div className={classes.baseCheckboxes}>
                      <label>
                        <input
                          type="checkbox"
                          checked={currentRunners[0]}
                          onChange={(e) => {
                            const newRunners: [boolean, boolean, boolean] = [
                              ...currentRunners,
                            ] as [boolean, boolean, boolean];
                            newRunners[0] = e.target.checked;
                            setCurrentRunners(newRunners);
                          }}
                        />
                        1B
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={currentRunners[1]}
                          onChange={(e) => {
                            const newRunners: [boolean, boolean, boolean] = [
                              ...currentRunners,
                            ] as [boolean, boolean, boolean];
                            newRunners[1] = e.target.checked;
                            setCurrentRunners(newRunners);
                          }}
                        />
                        2B
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={currentRunners[2]}
                          onChange={(e) => {
                            const newRunners: [boolean, boolean, boolean] = [
                              ...currentRunners,
                            ] as [boolean, boolean, boolean];
                            newRunners[2] = e.target.checked;
                            setCurrentRunners(newRunners);
                          }}
                        />
                        3B
                      </label>
                    </div>
                  </div>

                  <div className={classes.controlGroup}>
                    <label>Outs:</label>
                    <select
                      value={currentOuts}
                      onChange={(e) => {
                        const newOuts = parseInt(e.target.value, 10);
                        setCurrentOuts(newOuts);
                      }}
                    >
                      <option value="0">0</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </div>
                </div>
              </div>

              <button
                className={`${classes.submitScoresButtonLarge} ${isSuccess ? classes.submitScoresSuccess : ''}`}
                onClick={handleSubmitScores}
                disabled={isSubmitting}
              >
                {isSuccess ? 'Success!' : 'Submit Scores'}
              </button>

              <button
                className="btn btn-danger btn-lg"
                onClick={() => {
                  if (
                    window.confirm(
                      'Are you sure you want to finish this game? This action cannot be undone.',
                    )
                  ) {
                    finishGame();
                  }
                }}
              >
                Finish
              </button>
            </>
          )}

          {game.state === 'finished' && (
            <>
              <h2 className={classes.teamName}>Game Finished</h2>
              <div className={classes.finalScore}>
                {game.away_team}: {totalAwayScore} vs {game.home_team}:{' '}
                {totalHomeScore}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
